<script setup lang="ts">
    import { computed, onMounted, ref, watch, type Ref } from 'vue'
    import { useRoute, useRouter, onBeforeRouteLeave } from 'vue-router'
    import { EditorState, generateName } from '@common/Common'
    import CorporateNavigation from '@common/Components/CorporateNavigation.vue'
    import { guid } from '@common/Guid'
    import { MonthYear, MonthYearHelpers, ValidationError } from '@common/types'
    import { useResourceMetricConfigurationQueryV1 } from '@Quest/Metrics/queries-vue-composables'
    import { useCreateResourceMetricCommandV1, useUpdateResourceMetricCommandV1 } from '@Quest/Metrics/commands-vue-composables'
    import type { ResourceMetricConfigurationReadModel } from '@Quest/Metrics/readmodels'
    import { ValueBalanceType, ValueCommitmentType } from '@Quest/Metrics/types'
    import MetricTimeWindowSettings from '@Quest/Metrics/Components/MetricTimeWindowSettings.vue'
    import MetricScheduleDependentSettings from '@Quest/Metrics/Components/MetricScheduleDependentSettings.vue'

    const route = useRoute()
    const router = useRouter()

    const workspaceId = computed(() => Number(route.params?.workspaceId ?? 0))
    const strategyId = computed(() => String(route.params?.strategyId ?? ''))
    const pageMode = computed(() => route.meta.mode as EditorState)
    const isEditMode = computed(() => pageMode.value === EditorState.Editor)

    const {
        runResourceMetricConfigurationQueryV1,
        modelResourceMetricConfigurationQueryV1
    } = useResourceMetricConfigurationQueryV1(workspaceId, strategyId)

    const rm = modelResourceMetricConfigurationQueryV1 as Ref<ResourceMetricConfigurationReadModel | null>

    const {
        invokeCreateResourceMetricCommandV1,
        validationErrorsCreateResourceMetricCommandV1,
        onSucceedCreateResourceMetricCommandV1
    } = useCreateResourceMetricCommandV1(workspaceId, strategyId)

    const {
        invokeUpdateResourceMetricCommandV1,
        validationErrorsUpdateResourceMetricCommandV1,
        onSucceedUpdateResourceMetricCommandV1
    } = useUpdateResourceMetricCommandV1(workspaceId, strategyId)

    const initialized = ref(false)
    const errors = ref<ValidationError[]>([])
    const isSaving = ref(false)
    const allowRouteLeave = ref(false)
    const isScheduleDependent = ref(false)

    const name = ref<string | null>(null)
    const description = ref<string | null>(null)
    const resourceId = ref<string | null>(null)

    const windowStartOffsetInMonths = ref<number | null>(null)
    const windowLengthInMonths = ref<number | null>(null)

    const startDateBoundary = ref<MonthYear | null>(null)
    const endDateBoundary = ref<MonthYear | null>(null)
    const defaultProjectStartDate = ref<MonthYear | null>(null)

    const valueBalanceType = ref<ValueBalanceType>(ValueBalanceType.Costs)
    const applyBenefitsConfidenceFactor = ref(false)
    const valueCommitmentType = ref<ValueCommitmentType>(ValueCommitmentType.Total)

    const pageHeading = computed(() => isEditMode.value ? "Edit the metric" : "New resource metric")
    const saveButtonText = computed(() => isSaving.value ? "Saving..." : "Save changes")
    const nameError = computed(() => errors.value.find(e => e.ErrorCode.startsWith("Name."))?.Error ?? null)

    const hasChanges = computed(() =>
    {
        if (!isEditMode.value) return true

        const model = rm.value
        if (!model) return true

        const scheduleEnabled = isScheduleDependent.value
        const scheduleExists = model.ScheduleDependentSettings != null

        if (scheduleEnabled !== scheduleExists)
            return true

        // base fields (common)
        if (
            name.value !== model.Name ||
            description.value !== model.Description ||
            resourceId.value !== model.ResourceId ||
            valueBalanceType.value !== model.ValueBalanceType ||
            windowStartOffsetInMonths.value !== model.WindowStartOffsetInMonths ||
            windowLengthInMonths.value !== model.WindowLengthInMonths
        )
        {
            return true
        }

        if (!scheduleEnabled)
            return false

        const s = model.ScheduleDependentSettings

        return (
            applyBenefitsConfidenceFactor.value !== s?.ApplyBenefitsConfidenceFactor ||
            valueCommitmentType.value !== s?.ValueCommitmentType ||
            !MonthYearHelpers.areEqual(defaultProjectStartDate.value, s?.DefaultProjectStartDate) ||
            !MonthYearHelpers.areEqual(startDateBoundary.value, s?.StartDateBoundary) ||
            !MonthYearHelpers.areEqual(endDateBoundary.value, s?.EndDateBoundary)
        )
    })


    function handleCancel()
    {
        if (isEditMode.value)
            router.go(0)
        else
            router.go(-1)
    }

    function handleBack()
    {
        router.go(-1)
    }

    function handleSave()
    {
        isSaving.value = true
        errors.value = []

        const payload = {
            id: rm.value?.Id ?? guid(),
            name: name.value,
            description: description.value,
            resourceId: resourceId.value,
            valueBalanceType: valueBalanceType.value,
            windowStartOffsetInMonths: windowStartOffsetInMonths.value,
            windowLengthInMonths: windowLengthInMonths.value,
            isScheduleDependent: isScheduleDependent.value,
            defaultProjectStartDate: defaultProjectStartDate.value,
            startDateBoundary: startDateBoundary.value,
            endDateBoundary: endDateBoundary.value,
            valueCommitmentType: valueCommitmentType.value,
            applyBenefitsConfidenceFactor: applyBenefitsConfidenceFactor.value
        }

        if (isEditMode.value && rm.value)
        {
            invokeUpdateResourceMetricCommandV1(payload)
        }
        else
        {
            invokeCreateResourceMetricCommandV1(payload)
        }
    }


    onMounted(() =>
    {
        document.title = "Resource Metric Configuration"
        runResourceMetricConfigurationQueryV1({ MetricId: route.params.metricId ?? null })
    })

    watch(rm, (model) =>
    {
        if (!model || initialized.value) return

        name.value = isEditMode.value ? model.Name : generateName(model.Name)
        description.value = model.Description
        resourceId.value = model.ResourceId ?? null
        valueBalanceType.value = model.ValueBalanceType
        windowStartOffsetInMonths.value = model.WindowStartOffsetInMonths
        windowLengthInMonths.value = model.WindowLengthInMonths

        isScheduleDependent.value = model.ScheduleDependentSettings != null
        applyBenefitsConfidenceFactor.value = model.ScheduleDependentSettings?.ApplyBenefitsConfidenceFactor ?? false
        valueCommitmentType.value = model.ScheduleDependentSettings?.ValueCommitmentType ?? ValueCommitmentType.Total

        defaultProjectStartDate.value = MonthYearHelpers.copyNullable(model.ScheduleDependentSettings?.DefaultProjectStartDate)
        startDateBoundary.value = MonthYearHelpers.copyNullable(model.ScheduleDependentSettings?.StartDateBoundary)
        endDateBoundary.value = MonthYearHelpers.copyNullable(model.ScheduleDependentSettings?.EndDateBoundary)

        initialized.value = true
    })

    onSucceedCreateResourceMetricCommandV1(() =>
    {
        allowRouteLeave.value = true
        router.go(-1)
    })

    onSucceedUpdateResourceMetricCommandV1(() =>
    {
        allowRouteLeave.value = true
        router.go(0)
    })

    watch(validationErrorsCreateResourceMetricCommandV1, (validationErrors) =>
    {
        if (!validationErrors || validationErrors.length === 0) return
        errors.value = validationErrors
        isSaving.value = false
    })

    watch(validationErrorsUpdateResourceMetricCommandV1, (validationErrors) =>
    {
        if (!validationErrors || validationErrors.length === 0) return
        errors.value = validationErrors
        isSaving.value = false
    })

    onBeforeRouteLeave((to, from, next) =>
    {
        if (allowRouteLeave.value)
        {
            next()
            return
        }

        if (hasChanges.value && !isSaving.value)
        {
            if (confirm('You have unsaved changes. Do you want to save them before leaving?'))
            {
                handleSave()
            }
            else
            {
                next()
            }
        } else
        {
            next()
        }
    })
</script>


<template>
    <div>
        <CorporateNavigation ActivePage="metrics" />

        <div v-if="initialized" class="container" style="margin-top: 80px; padding-top: 40px;">
            <div class="row">
                <div class="col-lg-6">
                    <h6>
                        <a class="text-dark" @click="handleBack" data-testid="resource-metric-config-back">
                            <i class="fa fa-angle-double-left" aria-hidden="true"></i> Back
                        </a>
                    </h6>
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">{{pageHeading}}</h1>
                </div>

                <div class="col-lg-6 text-right align-self-end">
                    <button type="submit" form="resource-metric-configuration-form" :disabled="isSaving" data-testid="resource-metric-config-save"
                            :class="hasChanges ? 'btn btn-warning' : 'btn btn-outline-secondary'" style="padding: 0.7em 2em;">
                        {{ saveButtonText }}
                    </button>
                    <button v-if="hasChanges" type="button" class="btn btn-outline-secondary" style="padding: 0.7em 2em;" @click="handleCancel" data-testid="resource-metric-config-cancel">
                        Cancel
                    </button>
                </div>
            </div>

            <hr class="mb-5" />

            <form id="resource-metric-configuration-form" @submit.prevent="handleSave" class="mb-4" data-testid="resource-metric-config-form">
                <div class="form-group mb-4">
                    <label>Metric Name:</label>
                    <input v-model.trim="name" type="text" class="form-control" required maxlength="100" minlength="2" data-testid="resource-metric-config-name" />
                    <div class="error">{{nameError}}</div>
                </div>

                <div class="form-group mb-4">
                    <label>Metric Description:</label>
                    <textarea v-model.trim="description" class="form-control" data-testid="resource-metric-config-description"></textarea>
                </div>

                <div class="form-row">
                    <div class="form-group mb-4 col">
                        <label for="resource" class="form-label">Measured Resource:</label>
                        <select id="resource" v-model="resourceId" class="form-control" required data-testid="resource-metric-config-resource">
                            <option disabled :value="null">-- Select a Resource --</option>
                            <option v-for="r in rm.Resources" :key="r.Id" :value="r.Id">{{r.Name}}</option>
                        </select>
                    </div>

                    <div class="form-group mb-4 col">
                        <label for="valueBalanceType" class="form-label">Resource Data Type:</label>
                        <select id="valueBalanceType" v-model="valueBalanceType" class="form-control" data-testid="resource-metric-config-value-balance-type">
                            <option value="Costs">Allocations</option>
                            <option value="Benefits">Savings</option>
                            <option value="Net">Balance (Allocations - Savings)</option>
                        </select>
                    </div>
                </div>

                <MetricTimeWindowSettings v-model:startOffset="windowStartOffsetInMonths"
                                          v-model:length="windowLengthInMonths" />


                <MetricScheduleDependentSettings :canDisable="true"
                                                 v-model:isScheduleDependent="isScheduleDependent"
                                                 v-model:defaultProjectStartDate="defaultProjectStartDate"
                                                 v-model:startDateBoundary="startDateBoundary"
                                                 v-model:endDateBoundary="endDateBoundary"
                                                 v-model:applyBenefitsConfidenceFactor="applyBenefitsConfidenceFactor"
                                                 v-model:valueCommitmentType="valueCommitmentType" />
            </form>
        </div>
    </div>
</template>
